// Types
import { CreateMatchupInput, MatchupCorner } from 'api/graphql-global-types';
import { TicketMatchupFields } from 'api/ticketing/types/TicketMatchupFields';
import { GetTicketEventVoteResults_getTicketEventVoteResults } from 'api/ticketing/types/GetTicketEventVoteResults';
// Helpers
import { getPersonName } from 'pages/Events/helpers';

type Matchup = TicketMatchupFields;
type Competitor = NonNullable<Matchup['competitors']>[number];
type VoteResults = GetTicketEventVoteResults_getTicketEventVoteResults;

export enum CompetitorMode {
  Store = 'store',
  Invite = 'invite',
}

export type CompetitorFormValues = {
  mode: CompetitorMode;
  storeId?: string;
  /** Shown for a store competitor, sent for an invitee. */
  name?: string;
  invitedEmail?: string;
};

export type BoutFormValues = {
  matchupId?: string;
  isMainEvent?: boolean;
  category?: string;
  rounds?: number | null;
  roundMinutes?: number | null;
  competitorA: CompetitorFormValues;
  competitorB: CompetitorFormValues;
};

export const MAX_BOUTS = 30;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const createEmptyBout = (): BoutFormValues => ({
  isMainEvent: false,
  competitorA: { mode: CompetitorMode.Store },
  competitorB: { mode: CompetitorMode.Store },
});

export const isCompetitorSet = (competitor?: CompetitorFormValues): boolean =>
  competitor?.mode === CompetitorMode.Invite
    ? Boolean(competitor.name?.trim())
    : Boolean(competitor?.storeId);

const isBoutStarted = (bout: BoutFormValues): boolean =>
  isCompetitorSet(bout.competitorA) || isCompetitorSet(bout.competitorB);

export const getCompetitorName = (competitor?: Competitor | null): string =>
  competitor?.name || getPersonName(competitor?.store, 'TBD');

const toCompetitorInput = (
  corner: MatchupCorner,
  competitor: CompetitorFormValues
) =>
  competitor.mode === CompetitorMode.Invite
    ? {
        corner,
        name: competitor.name?.trim() || null,
        invitedEmail: competitor.invitedEmail?.trim() || null,
      }
    : { corner, storeId: competitor.storeId || null };

/** Empty bouts are dropped; ids go back so a bout keeps its fan votes. */
export const mapBoutsToMatchupsInput = (
  bouts: BoutFormValues[] = []
): CreateMatchupInput[] =>
  bouts.filter(isBoutStarted).map((bout, index) => ({
    ...(bout.matchupId ? { id: bout.matchupId } : {}),
    order: index,
    isMainEvent: Boolean(bout.isMainEvent),
    category: bout.category?.trim() || null,
    rounds: bout.rounds || null,
    roundMinutes: bout.roundMinutes || null,
    competitors: [
      toCompetitorInput(MatchupCorner.A, bout.competitorA),
      toCompetitorInput(MatchupCorner.B, bout.competitorB),
    ],
  }));

const toCompetitorFormValues = (
  competitor?: Competitor
): CompetitorFormValues =>
  competitor?.storeId
    ? {
        mode: CompetitorMode.Store,
        storeId: competitor.storeId,
        name: getCompetitorName(competitor),
      }
    : {
        mode: competitor?.name ? CompetitorMode.Invite : CompetitorMode.Store,
        name: competitor?.name || undefined,
      };

export const mapMatchupsToBouts = (matchups: Matchup[]): BoutFormValues[] =>
  [...matchups]
    .sort((a, b) => a.order - b.order)
    .map((matchup) => ({
      matchupId: matchup.id,
      isMainEvent: matchup.isMainEvent,
      category: matchup.category || undefined,
      rounds: matchup.rounds,
      roundMinutes: matchup.roundMinutes,
      competitorA: toCompetitorFormValues(
        matchup.competitors?.find(({ corner }) => corner === MatchupCorner.A)
      ),
      competitorB: toCompetitorFormValues(
        matchup.competitors?.find(({ corner }) => corner === MatchupCorner.B)
      ),
    }));

/** Same rules as web: a started bout needs both corners, invite emails must be valid, nobody fights twice. */
export const getMatchcardError = (
  bouts: BoutFormValues[] = []
): string | undefined => {
  const missing: string[] = [];
  const invalidEmails: string[] = [];
  const seen = new Map<string, string>();
  let duplicate: string | undefined;

  bouts.forEach((bout, index) => {
    if (!isBoutStarted(bout)) {
      return;
    }

    (['A', 'B'] as const).forEach((corner) => {
      const competitor = corner === 'A' ? bout.competitorA : bout.competitorB;
      const where = `Bout ${index + 1}, Competitor ${corner}`;

      if (!isCompetitorSet(competitor)) {
        missing.push(`Competitor ${corner} in Bout ${index + 1}`);
        return;
      }

      const email = competitor.invitedEmail?.trim();
      if (
        competitor.mode === CompetitorMode.Invite &&
        email &&
        !EMAIL_PATTERN.test(email)
      ) {
        invalidEmails.push(`${competitor.name} (${where}): "${email}"`);
      }

      if (competitor.mode === CompetitorMode.Store && competitor.storeId) {
        if (seen.has(competitor.storeId) && !duplicate) {
          duplicate = `${
            competitor.name || 'The same person'
          } is on the card twice (${seen.get(
            competitor.storeId
          )} and ${where}).`;
        }
        seen.set(competitor.storeId, where);
      }
    });
  });

  if (missing.length) {
    return `${missing.join('; ')} ${
      missing.length === 1 ? "isn't" : "aren't"
    } added yet. Pick someone on MILLIONS or invite them by name.`;
  }

  if (invalidEmails.length) {
    return `Invalid invite email for ${invalidEmails.join(', ')}`;
  }

  return duplicate;
};

export type VoteBreakdown = {
  total: number;
  percentA: number;
  percentB: number;
};

export const getVoteBreakdown = (
  matchup: Matchup,
  results: VoteResults[] = []
): VoteBreakdown | null => {
  const result = results.find(({ matchupId }) => matchupId === matchup.id);

  if (!result || !result.total) {
    return null;
  }

  const votesFor = (corner: MatchupCorner) => {
    const competitor = matchup.competitors?.find(
      (item) => item.corner === corner
    );

    return (
      result.competitorVotes.find(
        ({ competitorId }) => competitorId === competitor?.id
      )?.votes || 0
    );
  };
  const votesA = votesFor(MatchupCorner.A);
  const votesB = votesFor(MatchupCorner.B);
  const percentA =
    votesA + votesB ? Math.round((votesA / (votesA + votesB)) * 100) : 0;

  return {
    total: result.total,
    percentA,
    percentB: votesA + votesB ? 100 - percentA : 0,
  };
};
